import { type CoreLevelPermissionProps } from '@/services/types/permissions/mockCorePermissions'
import { type PermissionsProps } from '@/services/types/permissions/mockPermissions'
import { type StoreLevelPermissionProps } from '@/services/types/permissions/mockStorePermissions'
import {
  CORE_LEVEL_PERMISSIONS_IDS,
  type CoreLevelFeatureNames,
} from '@/services/types/permissions/permissionFeatures/mockCorePermissionFeature'
import {
  STORE_LEVEL_PERMISSIONS_IDS,
  type StoreLevelFeatureNames,
} from '@/services/types/permissions/permissionFeatures/mockStorePermissionFeature'
import { create } from 'zustand'

export interface PermissionsActions {
  isEditable: (permission: StoreLevelPermissionProps, storeId: string) => boolean
  isHidden: (permission: StoreLevelPermissionProps, storeId: string) => boolean
  isViewOnly: (permission: StoreLevelPermissionProps, storeId: string) => boolean
  setPermissions: (permissions: PermissionsProps) => void
}

export interface PermissionsState {
  actions: PermissionsActions
  coreLevelPermissions: CoreLevelPermissionProps[]
  storeLevelPermissions: StoreLevelPermissionProps[]
  isLoading: boolean
}

const INITIAL_STATE: Omit<PermissionsState, 'actions'> = {
  coreLevelPermissions: [],
  storeLevelPermissions: [],
  isLoading: true,
}

export const usePermissionsStore = create<PermissionsState>()((set) => ({
  ...INITIAL_STATE,
  actions: {
    isEditable: (permission, storeId) => {
      const { edit } = permission
      return edit.includes(storeId)
    },
    isHidden: (permission, storeId) => {
      const { hide } = permission
      return hide.includes(storeId)
    },
    isViewOnly: (permission, storeId) => {
      const { edit, view } = permission
      return view.includes(storeId) && !edit.includes(storeId)
    },
    setPermissions: ({ permissionWithStores, permissions }) => {
      set(() => ({
        coreLevelPermissions: permissions,
        storeLevelPermissions: permissionWithStores,
        isLoading: false,
      }))
    },
  },
}))

export const usePermissionsActions = () => usePermissionsStore((state) => state.actions)

export const useCorePermissions = (key: CoreLevelFeatureNames): CoreLevelPermissionProps | null =>
  usePermissionsStore((state) => {
    const id = CORE_LEVEL_PERMISSIONS_IDS[key]
    const permission = state.coreLevelPermissions.find((permission) => permission.feature.id === id)
    return permission || null
  })

export const useStorePermissions = (
  key: StoreLevelFeatureNames,
): StoreLevelPermissionProps | null =>
  usePermissionsStore((state) => {
    const id = STORE_LEVEL_PERMISSIONS_IDS[key]
    const permission = state.storeLevelPermissions.find(
      (permission) => permission.feature.id === id,
    )
    return permission || null
  })

export const useStorePermissionsHelpers = () =>
  usePermissionsStore((state) => {
    const { isEditable, isHidden, isViewOnly } = state.actions
    return { isEditable, isHidden, isViewOnly }
  })
